// Shared "Cambiar rol" modal (PATCH /admin/users/:id/role via changeRole()).
// Used by both the Equipo table's row action (Users.tsx) and the
// user-details page's Acciones card (UserDetail.tsx) — one implementation,
// identical copy and behavior everywhere.

import { useEffect, useState } from 'react'

import { Button, FormField, Modal, Select, toast } from '../../../design-system/index.js'
import { apiErrorMessage } from '../../../lib/api-error.js'
import { changeRole } from '../users-data.js'

export interface RoleModalTarget {
  id: string
  name: string
  platform_role: string
}

export interface RoleModalProps {
  target: RoleModalTarget | null
  onClose: () => void
  onSuccess: () => void | Promise<void>
  roles: readonly { value: string; label: string }[]
}

export function RoleModal({ target, onClose, onSuccess, roles }: RoleModalProps): JSX.Element {
  const [roleValue, setRoleValue] = useState('')
  const [busy, setBusy] = useState(false)

  // Re-seed the picker every time a new target opens.
  useEffect(() => {
    if (target) setRoleValue(target.platform_role)
  }, [target])

  async function submit(): Promise<void> {
    if (!target) return
    setBusy(true)
    try {
      await changeRole(target.id, roleValue)
      toast.ok('Rol actualizado', { description: 'Se cerraron sus sesiones activas.' })
      onClose()
      await onSuccess()
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo cambiar el rol.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      open={target !== null}
      onClose={onClose}
      title="Cambiar rol"
      subtitle={
        target
          ? `Nuevo rol para ${target.name}. Solo un dueño puede nombrar administradores o dueños.`
          : 'Elige el nuevo rol.'
      }
      busy={busy}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={() => void submit()} loading={busy}>
            Guardar
          </Button>
        </>
      }
    >
      <FormField label="Rol" htmlFor="role-target">
        <Select
          id="role-target"
          value={roleValue}
          onChange={(e) => setRoleValue(e.target.value)}
          options={roles.map((r) => ({ value: r.value, label: r.label }))}
        />
      </FormField>
    </Modal>
  )
}
